import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { callAIStructured, AIGatewayError } from "./ai-gateway";
import { enforceQuota, incrementUsage, QuotaExceededError } from "./lib/quota";
import { CAPABILITIES } from "@/lib/quotas";

const FILLER_WORDS = [
  "um",
  "uh",
  "like",
  "you know",
  "basically",
  "actually",
  "literally",
  "sort of",
  "kind of",
  "right",
];

function countFillers(transcript: string): { total: number; breakdown: Record<string, number> } {
  const lower = ` ${transcript.toLowerCase().replace(/[.,?!;:]/g, " ")} `;
  const breakdown: Record<string, number> = {};
  let total = 0;
  for (const w of FILLER_WORDS) {
    const rx = new RegExp(`(?<=\\W)${w.replace(/ /g, "\\s+")}(?=\\W)`, "g");
    const matches = lower.match(rx);
    const n = matches ? matches.length : 0;
    if (n > 0) {
      breakdown[w] = n;
      total += n;
    }
  }
  return { total, breakdown };
}

function wordCount(transcript: string): number {
  return transcript.trim().split(/\s+/).filter(Boolean).length;
}

const STAR_TOOL = {
  name: "save_interview_analysis",
  description:
    "Score a behavioural interview answer using the STAR rubric. Each component is 0–5 (0 = absent, 5 = excellent). Provide a one-sentence rationale per component and overall coaching feedback.",
  parameters: {
    type: "object",
    properties: {
      sentiment: {
        type: "string",
        enum: ["confident", "neutral", "hesitant", "anxious", "enthusiastic"],
      },
      situation: { type: "number", minimum: 0, maximum: 5 },
      situationNote: { type: "string" },
      task: { type: "number", minimum: 0, maximum: 5 },
      taskNote: { type: "string" },
      action: { type: "number", minimum: 0, maximum: 5 },
      actionNote: { type: "string" },
      result: { type: "number", minimum: 0, maximum: 5 },
      resultNote: { type: "string" },
      overallFeedback: {
        type: "string",
        description: "2-3 sentences of concrete coaching for the next attempt.",
      },
    },
    required: [
      "sentiment",
      "situation",
      "situationNote",
      "task",
      "taskNote",
      "action",
      "actionNote",
      "result",
      "resultNote",
      "overallFeedback",
    ],
    additionalProperties: false,
  },
};

type StarResp = {
  sentiment: string;
  situation: number;
  situationNote: string;
  task: number;
  taskNote: string;
  action: number;
  actionNote: string;
  result: number;
  resultNote: string;
  overallFeedback: string;
};

export const analyzeInterview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      sessionId?: string;
      question: string;
      transcript: string;
      durationSec: number;
      audioPath?: string | null;
      jdId?: string | null;
    }) => {
      if (!input?.question || !input?.transcript) {
        throw new Error("question and transcript are required");
      }
      if (typeof input.durationSec !== "number" || input.durationSec <= 0) {
        throw new Error("durationSec must be > 0");
      }
      return input;
    },
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // Re-recording an existing session counts against rerecord quota; new sessions
    // count against interview_question.
    const capability = data.sessionId
      ? CAPABILITIES.interview_rerecord
      : CAPABILITIES.interview_question;
    try {
      await enforceQuota(supabase, userId, capability);
    } catch (e) {
      if (e instanceof QuotaExceededError) throw new Response(e.message, { status: e.status });
      throw e;
    }

    const words = wordCount(data.transcript);
    const minutes = data.durationSec / 60;
    const wpm = minutes > 0 ? Math.round(words / minutes) : 0;
    const { total: fillerCount, breakdown: fillerBreakdown } = countFillers(data.transcript);

    let star: StarResp;
    try {
      star = await callAIStructured<StarResp>({
        messages: [
          {
            role: "system",
            content:
              "You are an interview coach evaluating a candidate's behavioural answer using the STAR method (Situation, Task, Action, Result). Be honest and concrete.",
          },
          {
            role: "user",
            content: `Question: ${data.question}\n\nCandidate answer (verbatim transcript):\n${data.transcript}`,
          },
        ],
        tool: STAR_TOOL,
      });
    } catch (e) {
      if (e instanceof AIGatewayError)
        throw new Response(e.message, { status: e.status });
      throw e;
    }

    const starScore = (star.situation + star.task + star.action + star.result) / 4; // out of 5

    const row = {
      user_id: userId,
      jd_id: data.jdId ?? null,
      question: data.question,
      transcript: data.transcript,
      duration_sec: data.durationSec,
      wpm,
      filler_count: fillerCount,
      filler_breakdown: fillerBreakdown,
      sentiment: star.sentiment,
      star_score: starScore,
      star_breakdown: {
        situation: star.situation,
        situationNote: star.situationNote,
        task: star.task,
        taskNote: star.taskNote,
        action: star.action,
        actionNote: star.actionNote,
        result: star.result,
        resultNote: star.resultNote,
      },
      feedback: star.overallFeedback,
      audio_path: data.audioPath ?? null,
    };

    let sessionId = data.sessionId;
    if (sessionId) {
      const { error } = await supabase
        .from("interview_sessions")
        .update(row)
        .eq("id", sessionId)
        .eq("user_id", userId);
      if (error) throw new Response(error.message, { status: 500 });
    } else {
      const { data: ins, error } = await supabase
        .from("interview_sessions")
        .insert(row)
        .select("id")
        .single();
      if (error) throw new Response(error.message, { status: 500 });
      sessionId = ins.id;
    }

    await incrementUsage(userId, capability);

    return {
      sessionId,
      wpm,
      fillerCount,
      fillerBreakdown,
      sentiment: star.sentiment,
      starScore,
      starBreakdown: row.star_breakdown,
      feedback: star.overallFeedback,
    };
  });

export const listInterviewSessions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("interview_sessions")
      .select(
        "id, question, transcript, duration_sec, wpm, filler_count, filler_breakdown, sentiment, star_score, star_breakdown, feedback, audio_path, created_at",
      )
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(25);
    if (error) throw new Response(error.message, { status: 500 });
    return { sessions: data ?? [] };
  });

export const deleteInterviewSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => {
    if (!input?.id) throw new Error("id required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row } = await supabase
      .from("interview_sessions")
      .select("audio_path")
      .eq("id", data.id)
      .eq("user_id", userId)
      .maybeSingle();

    if (row?.audio_path) {
      await supabase.storage.from("interview-recordings").remove([row.audio_path]);
    }

    const { error } = await supabase
      .from("interview_sessions")
      .delete()
      .eq("id", data.id)
      .eq("user_id", userId);
    if (error) throw new Response(error.message, { status: 500 });
    return { ok: true };
  });

/**
 * Mint a short-lived signed URL for an interview recording so the user can
 * replay it. Free — no AI calls, just a Supabase storage signature.
 * RLS on the storage bucket guarantees the user can only sign their own files.
 */
export const getInterviewAudioUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { sessionId: string }) => {
    if (!input?.sessionId) throw new Error("sessionId required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase
      .from("interview_sessions")
      .select("audio_path")
      .eq("id", data.sessionId)
      .eq("user_id", userId)
      .maybeSingle();
    if (error) throw new Response(error.message, { status: 500 });
    if (!row?.audio_path) return { url: null as string | null };
    const { data: signed, error: sErr } = await supabase.storage
      .from("interview-recordings")
      .createSignedUrl(row.audio_path, 60 * 10); // 10 minutes
    if (sErr) throw new Response(sErr.message, { status: 500 });
    return { url: signed.signedUrl };
  });
